import { Injectable } from '@angular/core';
import {
  ApiService,
  CountryListService,
  DialogService,
  InputDatastoreService,
  UserService,
  ValuesExchangeProvider
} from '@nrwl/common-lib';
import { flatMap, forEach, isEqual, isNaN, map, parseInt } from 'lodash';

import { BaseWizardService } from '../base.wizard.service';
import { UserWizardStep } from './user.wizard.step';

@Injectable()
export class UserWizardService extends BaseWizardService {

  dialogServiceMsgMap = {
    'wizardService.new_user_group': 'new group',
    'wizardService.inv_user': 'new user',
    'wizardService.update_user': 'update_user'
  };

  intendedUseArray = [
    'Service Responsiveness',
    'Network Performance',
    'VoIP Quality'
  ];
  intendedUseChecked = [];
  stopLoad = false;

  constructor(apiService: ApiService,
              inputDatastoreService: InputDatastoreService,
              countryListService: CountryListService,
              dialogService: DialogService,
              readonly userService: UserService,
              protected readonly valuesExchange: ValuesExchangeProvider) {
    super(apiService, inputDatastoreService, countryListService, dialogService);

    this.stepClass = UserWizardStep;
  }

  //------------------------------------------------ Init config/data ----------------------------------------------------

  // eslint-disable-next-line
  openWizard(wizardId,
             wizardConfig,
             defaultParamsById = {},
             onSuccess = () => {
             },
             onFail = () => {
             }) {
    super.openWizard(wizardId, wizardConfig, defaultParamsById, onSuccess, onFail);
  }

  onApply() {
    return this.currentStep.output;
  }

  // eslint-disable-next-line
  _setDefaultParams(step, targetOutput, defaultParamsById: any = {}, load?) {
    const stepIdGroup = 'wizardService.remove_users_from_roles.group';
    //do not need to add users roles to output because only selected roles belong to output
    if (step.id === stepIdGroup
      || step.id === 'wizardService.add_users_to_roles.group') {
      delete defaultParamsById['roleIds'];
    }
    if (step.id === 'wizardService.add_user_to_groups.group'
      || step.id === 'wizardService.remove_user_from_group.group') {
      delete defaultParamsById['groupIds'];
    }
    if (step.id === 'wizardService.update_user.group') {
      if (defaultParamsById.intendedUse) {
        this.intendedUseChecked = defaultParamsById.intendedUse.split(',');
      }
    }
    if (step.id === stepIdGroup) {
      const userName = step.output.userNames[0];
      const isCurrentUser = this.isCurrentUser(userName);
      const manageUserId = 'manageUsersPermissions';
      this.userService.getUserInfo$(userName).subscribe((response) => {
        const userInfo = response.success;
        const groupsRoles = flatMap(userInfo.groups, (g => g.roles.map(r => r.id)));
        forEach(step.dataSource, (item: any) => {
          if (item.id === manageUserId && isCurrentUser) {
            item.disabled = true;
          }
          if (groupsRoles.indexOf(item.id) > -1) {
            item.disabled = true;
            item.effective = true;
          }
        });
        super._setDefaultParams(step, targetOutput, defaultParamsById);
        this.stopLoad = true;
      });
    } else {
      super._setDefaultParams(step, targetOutput, defaultParamsById);
      this.stopLoad = true;
    }
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  // eslint-disable-next-line
  onFinish(): any {
    const responseProcessed = this._saveWizardData();
    responseProcessed.subscribe(() => {
        this.valuesExchange.notifyListeners('user_admin_board', { value: 'update',
cacheBreaker: Date.now() });
      }
    );
    return responseProcessed;
  }

  // eslint-disable-next-line
  _handleErrorResponse(response, onFail) {
    this.isLoading = false;

    if (onFail) {
      onFail(response);
    }

    if (response.error && response.error.errors) {
      this.errors = map(response.error.errors, error => {
        const code = parseInt(error);
        return isNaN(code) ? error : this._parseError(code);
      });
    } else if (response.status && response.status === 500) {
      this.errors = ['There was an error processing your request.'];
    } else if (response.statusText) {
      this.errors = [this.statusText];
    }
  }

  _parseError(code: number): string {
    switch (code) {
      case 102:
        return 'Role conflict detected';
      case 101:
      case 103:
      case 104:
        return 'Permission denied';
      default:
        return code.toString();
    }
  }

  canUserManageUsersResults(): boolean {
    return this.userService.canManageUsersResults();
  }

  canManagePermissions(): boolean {
    return this.userService.canManagePermissions();
  }

  isCurrentUser(username: string): boolean {
    const currentUserName = this.userService.getUserName();
    return isEqual(currentUserName, username);
  }
}
